import type { MfaChallengeErrorKind } from "@canucci/base/auth";
/**
 * Default copy for LoginPage. Used when no translate function is provided
 * or when a key lookup returns empty. Keys match the contract consumed
 * by the shared component (aligned with canvas-ui login keys for compatibility).
 */
export const defaultLoginPageStrings: Record<string, string> = {
  "login.title": "Sign In",
  "login.subtitle": "Sign in to access your canvases.",
  "login.signInWithGoogle": "Sign in with Google",
  "login.signInWithMicrosoft": "Sign in with Microsoft",
  "login.signingIn": "Signing in...",
  "login.loading": "Loading...",
  "login.authenticatingDeepLink": "Authenticating and redirecting...",
  "login.dividerEmail": "Or continue with email",
  "login.dividerSignUpEmail": "or sign up with email",
  "login.dividerOr": "or",
  "login.dividerContinueWith": "Or continue with",
  "login.otherSignInOptions": "Other sign-in options",
  "login.ssoShort.google": "Google",
  "login.ssoShort.microsoft": "Microsoft",
  "login.form.emailLabel": "Email",
  "login.form.passwordLabel": "Password",
  "login.form.confirmPasswordLabel": "Confirm Password",
  "login.form.signIn": "Sign In",
  "login.form.signUp": "Sign Up",
  "login.form.signInTitle": "Welcome back",
  "login.form.signUpTitle": "Create your account",
  "login.form.subtitleSignIn": "Log in to your Collective Discovery account.",
  "login.form.subtitleSignUp": "Get started in under a minute.",
  "login.form.noAccountPrompt": "New to Collective Discovery?",
  "login.form.hasAccountPrompt": "Already have an account?",
  "login.form.switchToSignIn": "Log in",
  "login.form.switchToSignUp": "Create an account",
  "login.form.createAccount": "Create Account",
  "login.form.forgotPassword": "Forgot password?",
  "login.forgotPassword.sent": "Check your email for a link to reset your password.",
  "login.forgotPassword.invalidEmail": "Enter your email address above first.",
  "login.forgotPassword.failed": "Something went wrong. Please try again later.",
  "login.forgotPassword.sending": "Sending...",
  "login.forgotPassword.title": "Reset your password",
  "login.forgotPassword.subtitle": "Enter the email you use for Collective Discovery and we'll send you a reset link.",
  "login.forgotPassword.backToLogin": "Back to log in",
  "login.forgotPassword.submit": "Send reset link",
  "login.forgotPassword.infoHint":
    "Check your inbox — the link expires in 30 minutes. If it doesn't arrive, look in spam or try again.",
  "login.brand.productName": "Collective Discovery",
  "login.brand.byline": "by Canucci",
  "login.brand.signupHeadline": "Your team knows things you don't know yet.",
  "login.brand.signupBody": "Surface what people really think — anonymously — and turn it into themes you can act on.",
  "login.brand.loginHeadline": "A steady hand in uncertain water.",
  "login.brand.loginBody":
    "Pick up where you left off — your sessions, themes and findings are right where you need them.",
  "login.brand.forgotHeadline": "It happens. Let's get you back in.",
  "login.brand.forgotBody": "We'll send a secure reset link to your work email. No password needed to start.",
  "login.brand.verifyHeadline": "Almost there.",
  "login.brand.verifyBody": "Confirm your email to activate your Collective Discovery account.",
  "login.brand.pendingHeadline": "We're reviewing your account.",
  "login.brand.pendingBody": "Your email is confirmed. We'll notify you when your account is ready.",
  "login.brand.deactivatedHeadline": "This account is inactive.",
  "login.brand.deactivatedBody": "Contact your administrator or support if you need access restored.",
  "login.brand.mfaHeadline": "One more step.",
  "login.brand.mfaBody": "Open the app on your phone and enter the code to finish signing in.",
  "login.brand.revertMfaHeadline": "Keep your account safe.",
  "login.brand.revertMfaBody": "Remove a 2-step verification method you didn't add yourself.",
  "login.mfa.title": "Two-step verification",
  "login.mfa.subtitle": "Enter the 6-digit code from the app on your phone.",
  "login.mfa.codeLabel": "Authentication code",
  "login.mfa.verify": "Verify",
  "login.mfa.backToSignIn": "Back to sign in",
  "login.mfa.invalidCodeFormat": "Enter the 6-digit code from the app on your phone.",
  "login.mfa.invalidCode":
    "That code didn't work. Codes can only be used once and change every 30 seconds \u2014 wait for the next one and try again.",
  "login.mfa.sessionExpired": "Your sign-in session expired. Please sign in again.",
  "login.mfa.rateLimited": "Too many attempts. Wait a minute, then try again.",
  "login.mfa.genericError": "Could not verify the code. Please try again.",
  "login.mfa.lostAccess": "No longer have your phone? Contact {{supportEmail}} and we'll help you back in.",
  "login.errors.userNotFound": "We couldn't find an account for that email. Check the address or sign up instead.",
  "login.errors.wrongPassword": "That password doesn't match this email. Try again.",
  "login.errors.invalidCredential": "We couldn't sign you in with that email and password. Check them and try again.",
  "login.errors.emailInUse": "That email is already registered. Try signing in instead.",
  "login.errors.passwordsDoNotMatch": "Passwords do not match.",
  "login.errors.userNotFoundContactAdmin": "User not found. Please contact your administrator.",
  "login.errors.unexpectedSignIn": "An unexpected error occurred during sign-in.",
  "login.errors.unexpectedSignUp": "An unexpected error occurred during sign-up.",
  "login.errors.googleSignInFailed": "Google sign-in failed",
  "login.errors.microsoftSignInFailed": "Microsoft sign-in failed. Please try again.",
  "login.validation.invalidEmail": "Invalid email",
  "login.validation.passwordMinLength": "Password must be at least 6 characters",
  "login.verifyEmail.title": "Check Your Email",
  "login.verifyEmail.message": "We sent a verification link to {{email}}. Open it to activate your account.",
  "login.verifyEmail.instructions":
    "Check your inbox and click the link to activate your account. It may take a few minutes to arrive.",
  "login.verifyEmail.resend": "Resend verification email",
  "login.verifyEmail.changeAccount": "Sign in with a different account",
  "login.verifyEmail.sent": "Verification email sent.",
  "login.verifyEmail.resendFailed": "Failed to send. You may need to wait a minute before trying again.",
  "login.emailConfirmed.title": "Email confirmed",
  "login.pendingApproval.title": "Approval Pending",
  "login.pendingApproval.message":
    "Your email is confirmed and your account is awaiting approval from Canucci. Please come back later or contact support if needed.",
  "login.pendingApproval.backToSignIn": "Back to sign in",
  "login.deactivated.title": "Account deactivated",
  "login.deactivated.message":
    "Your account has been deactivated. Please contact your administrator if you believe this is an error.",
  "login.errorTitle": "Error",
  "login.footer.agreement": "By signing in, you agree to our Terms of Service and Privacy Policy.",
};

/** Keys used by LoginPage (for documentation and consistency). */
export const LOGIN_PAGE_I18N_KEYS = Object.keys(defaultLoginPageStrings) as readonly string[];

/**
 * Where each challenge failure reported by `@canucci/base/auth` gets its copy. Shared auth
 * emits the kind; this is the Mantine side of the translation.
 */
export const MFA_CHALLENGE_I18N_KEYS: Record<MfaChallengeErrorKind, string> = {
  invalid_code: "login.mfa.invalidCode",
  expired: "login.mfa.sessionExpired",
  rate_limited: "login.mfa.rateLimited",
  generic: "login.mfa.genericError",
};
